import { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { unwrapArray } from '../lib/api-envelope';

const STORAGE_KEY = 'chat_last_checked';
const POLL_INTERVAL = 10_000; // 10 seconds
const PRIVILEGED_ROLES = ['admin', 'dispatcher', 'office_staff', 'supervisor'];

export interface UnreadJob {
  jobId: string;
  jobNumber: string;
  latestMessage: string;
  senderName: string;
  unreadCount: number;
  createdAt: string;
}

export interface GlobalChatState {
  unreadJobs: UnreadJob[];
  totalUnread: number;
  latestNotification: UnreadJob | null;
  clearNotification: () => void;
}

export function useGlobalChat(): GlobalChatState {
  const { user, isAuthenticated } = useAuth();
  const [unreadJobs, setUnreadJobs] = useState<UnreadJob[]>([]);
  const [latestNotification, setLatestNotification] = useState<UnreadJob | null>(null);
  const prevLatestRef = useRef<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isPrivileged =
    isAuthenticated && user?.role && PRIVILEGED_ROLES.includes(user.role);

  const getLastChecked = useCallback((): string => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) return stored;
    } catch {}
    // Default: 24 hours ago
    return new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
  }, []);

  const poll = useCallback(async () => {
    try {
      const since = getLastChecked();
      const res = await api.chat.unreadSummary(since);
      const jobs = unwrapArray<UnreadJob>(res.data);

      setUnreadJobs(jobs);

      // Detect new messages by comparing the latest createdAt
      if (jobs.length > 0) {
        const newest = jobs[0]; // already sorted DESC by createdAt
        if (prevLatestRef.current && newest.createdAt > prevLatestRef.current) {
          setLatestNotification(newest);
        }
        prevLatestRef.current = newest.createdAt;
      }
    } catch {
      // silently ignore poll failures
    }
  }, [getLastChecked]);

  const clearNotification = useCallback(() => {
    setLatestNotification(null);
  }, []);

  // Mark as checked — update localStorage timestamp
  // We update on each successful poll so the "since" window moves forward
  // only when the user explicitly dismisses / clicks through.
  // For now we keep the since stable until the user interacts.

  useEffect(() => {
    if (!isPrivileged) return;

    // Initial poll immediately
    poll();

    intervalRef.current = setInterval(poll, POLL_INTERVAL);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isPrivileged, poll]);

  // Initialize prevLatestRef on first successful fetch to avoid a false notification
  useEffect(() => {
    if (!isPrivileged) return;
    // Set the ref on mount so first poll doesn't trigger a toast
    const since = getLastChecked();
    api.chat.unreadSummary(since)
      .then(res => {
        const jobs = unwrapArray<UnreadJob>(res.data);
        if (jobs.length > 0) {
          prevLatestRef.current = jobs[0].createdAt;
        }
        setUnreadJobs(jobs);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPrivileged]);

  const totalUnread = unreadJobs.reduce((sum, j) => sum + j.unreadCount, 0);

  return {
    unreadJobs,
    totalUnread,
    latestNotification,
    clearNotification,
  };
}
